import {action, autorun, makeAutoObservable, set, remove, observable, runInAction, get, has} from "mobx";

import WatsonApi from "../backends/WatsonApi";
import deepmerge from 'deepmerge'
import {convertFlatToTree, convertTreeToFlat} from "../utils/DataUtilities";
import {defaultJsonData} from "../utils/Settings";
import {capitalize} from "../utils/StringUtilities";
import {isJson} from "../components/admin/spinup/utilities";
import {getConfig} from "../_configs/AppConfig";
import {ExecutionEnvironment} from "./InterfaceStore";

const {publicRuntimeConfig} = getConfig();

export function mergeBlockItems(oldItems, newItems) {
    return [...oldItems, ...newItems].reduce((acc, current) => {
        if (!acc.find(c => c.id === current.id)) {
            acc.push(current);
        }
        return acc;
    }, [])
}

export const blockLookups = {
    about: ['is_description'],
    news: ['is_news'],
    events: ['is_event'],
    gallery: ['is_media'],
    reviews: ['is_review'],
    map: ['is_location'],
    quicklinks: ['is_quicklink'],
    html: ['is_html'],
    files_folders: ['is_file', 'is_folder'],
};

const allBlockLookups = [
    'is_description',
    'is_news',
    'is_event',
    'is_media',
    'is_review',
    'is_location',
    'is_quicklink',
    'is_file',
    'is_folder',
];

export enum fetchState {
    "PENDING SCHEMA",
    "PENDING RESPONSE",
    "SUCCESS",
    "FAILURE",
}

export class FetchState {
    public value;
    public message;

    constructor({value, message}: {value: fetchState, message: string}) {
        this.value = value;
        this.message = message;
        makeAutoObservable(this);
    }
    set({value, message}: {value: fetchState, message: string}) {
        this.value = value;
        this.message = message;
    }
}

export default class OrganizationStore {
    private userId = null;

    constructor(userStore) {
        if (ExecutionEnvironment.canUseDOM) { // don't leak memory!
            autorun(() => {
                this.setUserId(userStore.id)
            });
        }

        makeAutoObservable(this, {
            updateThemeSettings: action,
        });
    }

    setUserId = id => {
        this.userId = id;
    };

    organization: OrganizationTypeOrganization | Partial<OrganizationTypeOrganization> = {}; // title organization
    currentOrganization: OrganizationTypeOrganization | Partial<OrganizationTypeOrganization> = {};
    locations: LocationContentItem[] = [];
    _navigation: Navigation[] = [];

    _blocks = new Map();

    get hero() {
        return this.organization?.json_data?.settings.hero.map(h => {
            if (!/^(http|https):\/\//i.test(h.media_url)) {
                return {
                    ...h,
                    media_url: publicRuntimeConfig.CDN_URL + "/" + h.media_url,
                };
            }
            return h;
        }) || [];
    }

    get blocks() {
        return this._blocks;
    }

    set blocks(blocks) {
        this._blocks = blocks;
    }

    content_sources: ContentSource[] = [];
    children: OrganizationTypeOrganization[] = []; // list of pages or collections whose parents are current organization
    hostname = null;

    customNavigationIsDisabled = true;

    content_analytics = {};
    userSites = [];
    block_config = new Map();

    get navigation() {
        return this._navigation;
    }

    set navigation(flatArray) {
        this._navigation = flatArray;
    }

    get shouldUpdateSources() {
        return this.content_analytics &&
            Array.from(Object.keys(this.content_analytics))
                .filter(key => !['-1', 'total', 'published_total', 'unpublished_total', 'user_permissions'].includes(key))
                .length !== this.content_sources.filter(source => !source.state.includes('error')).length;
    }

    get navigationTree() {
        return convertFlatToTree(this._navigation);
    }

    setCurrentOrganization = (org: OrganizationTypeOrganization) => {
        this.currentOrganization = org;
    }

    updateThemeSettings = (obj, newThemeID = false) => {
        if (newThemeID && this.organization.json_data) {
            this.organization.json_data.settings.appearance.themeID = String(newThemeID);
        }
        const themeID = this.organization.json_data?.settings.appearance.themeID;
        if (themeID) {
            for (const k in obj) {
                if (obj.hasOwnProperty(k) && this.organization.json_data) {
                    this.organization.json_data.settings.appearance.themes[themeID][k] = obj[k];
                }
            }
        }
    };

    toggleBlockEnabled = block => {
        this.block_config.set(block, !this.block_config.get(block));

        this.updateSettings({block_config: this.block_config}, false);
    };

    updateBlockPositions = allBlocks => { // this doesn't need to interact with db because blocks will be ordered by block_config on reload
        this.block_config.clear();
        this.blocks.clear();

        allBlocks.forEach(item => {
            this.block_config.set(item.key, item.enabled);

            if (item.type !== 'hero') {
                this.blocks.set(item.key, item);
            }
        });

        // THIS IS IMPORTANT. Don't use settings route because Upsert will not maintain order of request
        this.updateOrganizationField('json_data', {
            ...this.organization.json_data,
            settings: {
                ...this.organization.json_data?.settings,
                //@ts-ignore
                block_config: Object.fromEntries(this.block_config),
            },
        });
    };

    fetchStates = observable.map();

    removeFetchState(requestId) {
        if (requestId) {
            remove(this.fetchStates, requestId);
        }
    }

    updateFetchState(requestId, state) {
        if (requestId) {
            if (has(this.fetchStates, requestId)) {
                get(this.fetchStates, requestId).set(state);
            } else {
                set(this.fetchStates, requestId, new FetchState(state));
            }
        }
    }

    async fetchClient (tag, fn, args = {}, options: {
        requestId?: string,
        requestSuccessMessage?: string,
        organization_id?: OrganizationTypeOrganization["id"],
        user_id?: string,
        spinup?: boolean,
    } = {}) {
        const {
            requestId = null, // ID for tracking progress of request
            requestSuccessMessage = "Beep, boop... all done, your site has been updated!",
            organization_id = this.currentOrganization?.id || this.organization.id,
            user_id = this.userId,
            spinup = false,
        } = options;

        try {
            this.updateFetchState(requestId, {value: fetchState["PENDING SCHEMA"]});
            const client = await WatsonApi(organization_id, user_id, spinup);
            this.updateFetchState(requestId, {value: fetchState["PENDING RESPONSE"]});

            const res = await client[tag][fn](args);
            this.updateFetchState(requestId, {value: fetchState.SUCCESS, message: requestSuccessMessage});
            return res;
        } catch (error) {
            let message = "";
            if (typeof error === 'string') {
                message = `Oops! ${error}`;
            } else if (isJson(error)) {
                message = `Oops! ${JSON.parse(error.statusText).detail}`
            } else if (isJson(error?.data)) {
                message = JSON.parse(error.data).detail;
            }
            this.updateFetchState(requestId, {value: fetchState.FAILURE, message});
        } finally {
            this.removeFetchState(requestId);
        }
    }

    deleteNavigation = async (node: Navigation) => {
        const includeAllChildren = this.navigation.filter(item => {
            return item.path.slice(0, node.path.length) === node.path;
        }).map(item => item.id);
        // use something like ^^ for getting all children and bulk deleting
        await this.fetchClient("navigations", "navigations_bulk_destroy", {
            data: includeAllChildren,
        });
        this.navigation = this._navigation.filter(item => !includeAllChildren.includes(item.id));
    };

    deleteContentSource = function* (id) {
        try {
            yield this.fetchClient("content-sources", "content_sources_delete", {
                id: id,
            });
            this.content_sources = this.content_sources.filter(item => item.id !== id);
        } catch (error) {
            console.debug(error);
        }
    }

    updateContentSource = function* (id, url) {
        yield this.fetchClient("content-sources", "content_sources_partial_update", {
            id: id,
            data: {
                "source_url": url,
            },
        });
        this.content_sources = this.content_sources.map(item => {
            if (item.id === id) {
                return {
                    ...item,
                    source_url: url,
                }
            }
            return item;
        });
    }

    updateBlockItems(blockType, rawData, clear) { // this is for updating blocks once new items are fetched
        const blockObj = this.blocks.get(blockType);

        if (clear) {
            blockObj.items = [];
        }

        blockObj.items = mergeBlockItems(blockObj.items, rawData.data.filter(item => blockObj.lookups.some(t => item[t])));

        if (blockType === 'events' || blockType === 'files_folders') {
            // update based on meta, because this reflects total entries for last request
            blockObj.total = rawData.meta.total;
        }

        this.blocks.set(blockType, blockObj);
    }

    fetchBlockItems = async (passedQuery, {
        collectionId = undefined,
    } = {}) => { // this is for fetching new data
        // query options are: blockType, page, page_size, gte, lte, published
        // blockType, page, size, gte = false, lte = false, published = true
        // default should be to see only published items
        // to see ALL items (including hidden), published should be TRUE
        // to CURATE, published should be FALSE
        const blockObj = this.blocks.get(passedQuery.blockType);
        let query: { collection_pk?: string, organization_pk?: BaseOrganization["id"], blockType?: string, page?: number } = {
            organization_pk: this.organization.id,
        };

        allBlockLookups.reduce((accumulator, currentValue) => {
            if (blockObj.lookups.includes(currentValue)) {
                query[currentValue] = true;
            }
            return query;
        }, query);

        query = {
            ...query,
            ...passedQuery,
        };

        try {
            const obj = await this.fetchClient("organizations", "organizations_content_items_list", query);

            if (obj?.data) {
                return this.updateBlockItems(query.blockType, JSON.parse(obj.data), query.page === 1); // if page is 1, it's safe to clear
            }
        } catch (error) {
            console.debug(error);
        }
    };

    updateBlocks(items) {
        // this should only be called once on page load
        // goal of this function is to create the high level blocks
        // this is done so that blocks are loaded in correct order

        this.block_config.forEach((value, key) => {
            if (key !== 'hero' && key !== "_c") {
                const _key = /html/.test(key) ? 'html' : key;
                const blockItems = items.filter(item => blockLookups[_key].some(b => item[b]));

                let title;
                if (_key === 'html') {
                    title = this.organization.json_data?.settings.htmlBlocks[key].title;
                } else if (_key === 'files_folders') {
                    title = 'Files & Folders'
                } else {
                    title = capitalize(_key);
                }

                this.blocks.set(key, {
                    id: `realblock-${key}`,
                    key: key, // this is the key that is used for the grabbing this item from the Map
                    title: title,
                    type: _key, // this is the actual type (i.e. 'html')
                    items: blockItems,
                    lookups: blockLookups[_key],
                    total: blockItems.length,
                    blank: false,
                    grid_size_x: 2,
                });

                if (blockItems.length === 0 && _key !== 'html' && _key !== 'about') {
                    this.block_config.set(key, false)
                }
            }
        });
    }

    createHtmlBlock = () => {
        // const len = Array.from(this.blocks.values()).filter(block => block.type === 'html').length;

        let len = 0;
        while (this.organization.json_data?.settings.htmlBlocks[`html-${len}`]) {
            len++;
        }

        const block_key = `html-${len}`;

        this.blocks.set(block_key, {
            id: `realblock-${block_key}`,
            key: block_key,
            title: 'HTML Block',
            type: 'html',
            items: [],
            enabled: true,
            lookup: 'is_html',
            total: 0,
            blank: false,
            grid_size_x: 2,
        });

        this.block_config.set(block_key, true);

        this.updateSettings({
            block_config: this.block_config,
            htmlBlocks: {
                ...this.organization.json_data?.settings.htmlBlocks,
                [`html-${len}`]: {
                    'title': 'HTML Block',
                    'html': '',
                },
            },
        });
    };

    deleteBlock = key => {
        this.blocks.set(key, {
            ...this.blocks.get(key),
            enabled: false,
        });

        this.block_config.delete(key);

        if (key.includes('html')) {
            delete this.organization.json_data?.settings.htmlBlocks[key];
            this.updateSettings(this.organization.json_data?.settings, false);
        }
    };

    buildOrganization(newOrg: OrganizationTypeOrganization) {
        let titleOrg = newOrg;

        if (titleOrg.categories && titleOrg.categories.length > 0) {
            newOrg.vertical = titleOrg.categories[0].path;
        } else {
            newOrg.vertical = 'generic';
        }

        const default_JSON_data = defaultJsonData(newOrg.vertical);

        titleOrg.json_data = deepmerge(default_JSON_data, titleOrg.json_data);

        if (
            newOrg.hasOwnProperty("titleOrganization") &&
            newOrg.titleOrganization !== null
        ) {
            titleOrg = newOrg.titleOrganization;
        }

        if (titleOrg.navigation && Array.isArray(titleOrg.navigation)) {
            // this map can be removed once we're totally on Watson, but for now there are some discrepancies between
            // the model field names of Yii and Watson, so this is here to keep things consistent
            this.navigation = titleOrg.navigation;
        }

        if (isJson(titleOrg.json_data.settings.block_config)) {
            titleOrg.json_data.settings.block_config = JSON.parse(titleOrg.json_data.settings.block_config);
        }

        // as of mobx 6.0.0 we're saving block_config as type array[], which is what we want because that's the true serialized version of an array
        // but we need to support the older format of saving as an object
        if (Array.isArray(titleOrg.json_data.settings.block_config)) {
            titleOrg.json_data.settings.block_config.forEach(([k, v]) => this.block_config.set(k, v));
        } else {
            for (const k in titleOrg.json_data.settings.block_config) {
                if (titleOrg.json_data.settings.block_config.hasOwnProperty(k)) {
                    this.block_config.set(k, titleOrg.json_data.settings.block_config[k]);
                }
            }
        }


        // if there are any new blocks that aren't on current site, add them
        // sticking it here like this will ensure that correct ordering is maintained
        default_JSON_data.settings.block_config.forEach((value, key) => {
            if (!this.block_config.has(key)) {
                this.block_config.set(key, value);
            }
        });

        // hero should be an array, let's make it backwards compatible in case any objects or strings still exist
        if (titleOrg.json_data.settings.hero && !Array.isArray(titleOrg.json_data.settings.hero)) {
            titleOrg.json_data.settings.hero = [titleOrg.json_data.settings.hero]
        }

        this.organization = titleOrg;

        if (newOrg.content && Array.isArray(newOrg.content.items)) { // at this point content.items is not ordered
            this.updateBlocks(newOrg.content.items);
        }
    }

    getContentSources = function* (organization_id = this.organization.id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_content_sources_list", {
                "organization_pk": organization_id,
            });
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                return this.content_sources = data.data;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    getContentSource = function* (id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_content_sources_read", {
                "organization_pk": this.organization.id,
                "id": id,
            });
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                this.content_sources = this.content_sources.map(source => {
                    if (source.id === data.id) {
                        return data;
                    }
                    return source;
                });
            }
        } catch (error) {
            console.debug(error);
        }
    }

    // TODO: need to evaluate the best way to handle the "current" organization ID.  My understanding
    //   is that it should be currentOrganization.id always.
    getContentAnalysis = function* (orgId = this.currentOrganization?.id || this.organization.id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_content_analytics_list", {
                organization_pk: orgId,
                'cache-bust': Date.now(),
            }, {organization_id: orgId});
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                const user_permissions = data.user_permissions;
                if (user_permissions && orgId === this.currentOrganization?.id || this.organization.id) {
                    delete data.user_permissions.follower; // follower doesn't cascade - i.e. a follower of the title org is not necessary a follower of a sub-org, so it shouldn't be set here
                    delete data.user_permissions; // remove permissions from obj so it's not passed into content_analytics
                }
                this.content_analytics = data;
                return user_permissions;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    getChildOrganizations = function* () {
        try {
            const item = yield this.fetchClient("organizations", "organizations_list", {
                parent: this.organization.id,
                type__in: ["section", "department", "collection"],
                omit: ["url"],
            });
            if (item?.data) {
                return this.children = JSON.parse(item.data).data;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    createContentSource = async (dataObj) => {
        try {
            const obj = await this.fetchClient("content-sources", "content_sources_create", {
                "organization_pk": this.organization.id,
                data: {
                    ...dataObj,
                    "is_primary": false,
                    "refresh_rate": "01:00:00",
                    "organization": this.organization.id,
                },
            }, {
                requestId: "createContentSource",
                requestSuccessMessage: "Beep, boop... all done! Your new feed will appear below shortly!",
            });
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    createContentItem = function* (blockType, data) {
        // third_party_id should be custom_[description/location/etc] + timestamp (needs to be unique)
        // type should be custom.[description/location/etc]

        // fields for about text: description
        // fields for location: json_data with "location" key that has object of all the normal location stuff
        try {
            const obj = yield this.fetchClient('content-items', 'content_items_create', {
                "organization_pk": this.organization.id,
                data: {
                    ...data,
                    organization_id: this.organization.id,
                },
            });
            if (obj?.data) {
                const item = JSON.parse(obj.data);
                const blockObj = this.blocks.get(blockType);
                blockObj.items.push(item);
                this.blocks.set(blockType, blockObj);
                return this.getContentAnalysis();
            }
        } catch (error) {
            console.debug(error);
        }
    }

    deleteContentItem = function* (id, type) {
        try {
            yield this.fetchClient("organizations", "organizations_content_items_delete", {
                "organization_pk": this.organization.id,
                id: id,
            }, {
                requestId: "deleteContentItem",
            });
            const blockObj = this.blocks.get(type);
            this.blocks.set(type, {
                ...blockObj,
                items: [...blockObj.items.filter(item => item.id !== id)],
            });
            return this.getContentAnalysis();
        } catch (error) {
            console.debug(error);
        }
    }

    updateContentItem = function* (blockType, id, data) {
        try {
            const obj = yield this.fetchClient("content-items", "content_items_partial_update", {
                id: id,
                data: data,
            }, {
                requestId: "updateContentItem",
                requestSuccessMessage: 'Social feed added! It will appear below shortly.',
            });
            if (obj?.data) {
                const res = JSON.parse(obj.data);
                const blockObj = this.blocks.get(blockType);
                this.blocks.set(blockType, {
                    ...blockObj,
                    items: blockObj.items.map(item => {
                        if (item.id === res.id) {
                            return res;
                        }
                        return item;
                    }),
                });
                return this.getContentAnalysis();
            }
        } catch (error) {
            console.debug(error);
        }
    }

    bulkUpdateContentItems = function* (blockType, items) {
        try {
            const itemsArray = items;
            const obj = yield this.fetchClient("content-items", "content_items_bulk_update", {
                data: itemsArray,
            }, {
                requestId: "bulkUpdateContentItems",
                requestSuccessMessage: 'Social feed added! It will appear below shortly.',
            });
            if (obj?.data) {
                const data = JSON.parse(obj.data);
                const blockObj = this.blocks.get(blockType);

                this.blocks.set(blockType, {
                    ...this.blocks.get(blockType),
                    items: mergeBlockItems(blockObj.items, data),
                });

                return this.getContentAnalysis();
            }
        } catch (error) {
            console.debug(error);
        }
    }

    updateSettings = function* (update, showNotification = true) {
        try {
            const result = yield this.fetchClient("organizations", "organizations_settings_partial_update", {
                "organization_pk": this.organization.id,
                data: {...update},
            }, {
                requestId: showNotification ? "updateSettings": null,
            });
            if (result?.data) {
                const settings = {
                    ...this.organization.json_data.settings,
                    ...JSON.parse(result.data).settings,
                };
                this.organization.json_data.settings = settings;
                return settings;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    updateOrganizationField = function* (field, update, showNotification = true, org_id = this.organization.id) {
        try {
            const org = yield this.fetchClient("organizations", "organizations_partial_update", {
                "id": org_id,
                data: {
                    [field]: update,
                },
            }, {
                organization_id: org_id,
                requestId: showNotification ? "updateOrganizationField": null,
            });
            if (org?.data) {
                return this.organization = {...JSON.parse(org.data), ...this.organization};
            }
        } catch (error) {
            console.debug(error);
        }
    }

    refreshContentSource = async id => {
        try {
            await this.fetchClient("content-sources", "content_sources_refresh_create", {
                contentsource_pk: id,
            });
        } catch (error) {
            console.debug(error);
        }
    };

    getUserSites = function* () {
        try {
            const obj = yield this.fetchClient("users", "users_permissions_list", {
                'user_pk': this.userId,
            });
            if (obj?.data) {
                return this.userPermissions = JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    }

    imageUpload = function* (attribute, file, name) { // type == 'logo', 'hero', or 'picture'
        try {
            const obj = yield this.fetchClient("organizations", "organizations_file_upload_update", {
                organization_pk: this.organization.id,
                name,
                file: file,
                mime_type: file.type,
                attribute,
            });
            if (obj?.data) {
                const media = JSON.parse(obj.data);

                if (attribute === 'hero') {
                    this.organization.json_data.settings.hero.splice(0, 0, media)
                }

                return media;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    deleteMedia = function* (file_id, attribute) {
        try {
            yield this.fetchClient("organizations", "organizations_file_upload_delete", {
                organization_pk: this.organization.id,
                file_id,
                attribute,
            });

            if (attribute === 'hero') {
                this.organization.json_data.settings.hero = this.organization.json_data.settings.hero.filter(h => h.id !== file_id);
            }
        } catch (error) {
            console.debug(error);
        }
    }

    pollSource = async id => {
        try {
            const obj = await this.fetchClient('content-sources', "content_sources_states_list", {
                contentsource_pk: id,
            });
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    pollOrganization = async id => {
        try {
            const obj = await this.fetchClient("organizations", "organizations_states_list", {
                organization_pk: id,
            });
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    getHostname = function* () {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_hostnames_list", {
                organization_pk: this.organization.id,
            });
            if (obj?.data) {
                this.hostname = JSON.parse(obj.data).data;
                return this.hostname;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    patchHostname = function* (id, params, organization_id = this.organization.id) {
        try {
            const obj = yield this.fetchClient("organizations", "organizations_hostnames_partial_update", {
                organization_pk: organization_id,
                id: id,
                data: {...params},
            }, {
                organization_id,
                requestId: "patchHostname",
            });
            if (obj?.data) {
                this.hostname = JSON.parse(obj.data).data;
            }
        } catch (error) {
            console.debug(error);
        }
    }

    createHostname = async (hostname, schema = 'https', organization = this.organization.id) => {
        try {
            const obj = await this.fetchClient("hostnames", "hostnames_create", {
                data: {
                    scheme: schema,
                    hostname: hostname,
                    is_primary: true,
                    is_internal_primary: false,
                    organization: organization,
                },
            }, {organization_id: organization});
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    };

    bulkUpdateHostnames = async (data, organization_id = this.organization.id) => {
        try {
            const obj = await this.fetchClient("hostnames", "hostnames_bulk_partial_update", {
                data: data,
            }, {organization_id});
            if (obj?.data) {
                return JSON.parse(obj.data);
            }
        } catch (error) {
            console.debug(error);
        }
    }
}
